// Copyright 2022, Imprivata, Inc.  All rights reserved.
import { Flex, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useCreateInquiryMutation, useGetFhirPatientQuery } from '../../api/hooks';
import DemographicsCard from '../../components/demographics/DemographicsCard';
import { ErrorComponent } from '../../components/error/ErrorComponent';
import { PatientConsentStep } from '../../components/patient-consent/PatientConsentStep';
import { Button, HexagonSpinner } from '@imprivata-cloud/patient-access-ui-components';
import { useTranslation } from 'react-i18next';
const LaunchPage: React.FC = () => {
  const { t } = useTranslation();
  const [showConsentStep, setShowConsentStep] = useState(false);
  const [wasRestoredFromBFCache, setWasRestoredFromBFCache] = useState(false);
  const [searchParams] = useSearchParams();
  const { patient, isLoading, error: errorPatient } = useGetFhirPatientQuery();
  const { createInquiry, isPending, isSuccess, error: errorInquiry } = useCreateInquiryMutation();
  const launchError = searchParams.get('error-code');
  const [error, setError] = useState<string | null>(null);
  const isCreatingInquiry = isPending || (isSuccess && !wasRestoredFromBFCache);
  useEffect(() => {
    if (launchError) {
      setError(launchError);
      return;
    }
    if (errorPatient || errorInquiry) {
      const errorCode = (errorPatient || errorInquiry)?.error?.code;
      setError(errorCode || 'unknown');
    }
  }, [errorPatient, errorInquiry, launchError]);
  useEffect(() => {
    if (patient?.id && patient.id.length > 0) {
      setError('patient-enrolled');
    }
  }, [patient]);
  useEffect(() => {
  const handler = (event: { persisted: boolean }) => {
      if (event.persisted) {
        setWasRestoredFromBFCache(true);
      } else {
        setWasRestoredFromBFCache(false);
      }
    };
    window.addEventListener('pageshow', handler);
    return () => window.removeEventListener('pageshow', handler);
  }, []);
  const translateWithFallback = (key: string, fallback: string) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const handleConsentContinue = useCallback(() => {
    setShowConsentStep(false);
    setWasRestoredFromBFCache(false);
    createInquiry();
  }, [createInquiry]);

  if (showConsentStep) {
    return <PatientConsentStep onContinue={handleConsentContinue} />;
  }

  if (isCreatingInquiry) {
    return (
      <>
        <Typography.Title
          level={2}
          className="text-center title-light"
          data-testid="launch-loading-title"
        >
          {translateWithFallback(
            'launchPage.need-id-selfie-verify-identity',
            "We'll need a photo of your ID and a selfie to verify your identity",
          )}
        </Typography.Title>
        <HexagonSpinner data-testid="loading-spinner" />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Typography.Title
          level={2}
          className="text-center"
          data-testid="launch-loading-title"
        >
          {translateWithFallback(
            'launchPage.were-securely-loading-your-information-for-enrollment',
            "We're securely loading your information for enrollment.",
          )}
        </Typography.Title>
        <HexagonSpinner data-testid="loading-spinner" />
      </>
    );
  }

  if (error) {
    return <ErrorComponent cause={error} />;
  }

  if (patient) {
    return (
      <Flex align="center" gap={30} vertical>
        <Typography.Text
          className="m-0"
          data-testid="launch-demographics-text"
        >
          {t('launchPage.demographics-used-verify-selfie-valid-id-enroll')}
        </Typography.Text>
        <DemographicsCard
          isLoading={false}
          givenName={patient?.givenName}
          surname={patient?.surname}
          dateOfBirth={patient?.dateOfBirth}
          mrn={patient?.mrn}
          className="m-0"
          data-testid="demographics-card"
        />
        <Flex className="w-100 m-0" gap={8} justify="space-between" vertical>
          <Button
            variant="primary"
            onClick={() => setShowConsentStep(true)}
            label={t('launchPage.continue')}
            className="w-100"
            data-testid="launch-continue-btn"
          />
          <Typography.Text
            className="text-center m-0 mt-8p"
            data-testid="launch-enrollment-message"
          >
            {t('launchPage.enroll-in-person-registration-desk')}
          </Typography.Text>
        </Flex>
      </Flex>
    );
  }

  return null;
};
export default LaunchPage;
